import { Link, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import ConsoleTabs, { panelProps } from '../../components/ConsoleTabs.jsx';
import PageHeader from '../../components/PageHeader.jsx';
import Notice from '../../components/Notice.jsx';
import SectionCard from '../../components/SectionCard.jsx';
import StatusWord from '../../components/StatusWord.jsx';
import { customerSectionPath } from '../../lib/navigation.js';
import { notificationsPath } from '../../lib/watch-format.js';
import { tierLabel } from '../../lib/tier-labels.js';
import { customerStatusLabel, customerStatusTone } from '../../lib/status-labels.js';
import CompanyBranding from './CompanyBranding.jsx';
import { ConsentCard, DangerZone, DeploymentCard, IdentityCard } from './deployment-parts.jsx';
import StaffActivity from './StaffActivity.jsx';

/**
 * Settings (ENTRAID-347 redesign, ia-proposal 5.6, plan C6): Company Settings (Branding) and Deployment on one page.
 * Tabs, addressed by the path (settings/company, settings/microsoft-connection, settings/aws-deployment,
 * settings/danger-zone):
 *   Company               the company name, tier, customer id and status (moved from the old customer header) and Branding;
 *   Microsoft connection  the consent card (granted permissions, "Check permissions now", grant more);
 *   AWS deployment        the identity (Trading Partner ID, AWS account names) and the deployment card;
 *   IPC Global activity   what IPC Global staff did on this customer (ENTRAID-307), the customer's own view (hidden for
 *                         operators, who get counts only from the API);
 *   Danger zone           decommission or delete, operators only.
 * The same rules as before: `isOperator` (the navigation claim, false in "View as client") shows the operator controls
 * and the Danger zone; `operatorAccount` (the account's own flag, operatorWithoutAdmin) makes Branding read-only. The
 * API enforces every action either way.
 */

export const SETTINGS_TABS = Object.freeze([
  { id: 'company', label: 'Company' },
  { id: 'microsoft-connection', label: 'Microsoft connection' },
  { id: 'aws-deployment', label: 'AWS deployment' },
  { id: 'ipc-global-activity', label: 'IPC Global activity', customerOnly: true },
  { id: 'danger-zone', label: 'Danger zone', operatorOnly: true },
]);

const STATUS_TONE = Object.freeze({ green: 'good', amber: 'warn', red: 'bad', slate: 'off' });

/** The settings tabs a viewer sees: the Danger zone for operators only, IPC Global activity for the customer only. */
export function settingsTabs(isOperator) {
  const operator = isOperator === true;
  return SETTINGS_TABS.filter((t) => (!t.operatorOnly || operator) && (!t.customerOnly || !operator));
}

function CompanyTab({ customer, operatorAccount }) {
  const customerId = customer.customerId;
  return (
    <div className="max-w-3xl space-y-4">
      <SectionCard title="Company" testId="settings-company">
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-[180px_1fr]">
          <dt className="text-ipc-muted">Company name</dt>
          <dd className="text-ipc-ink">{customer.companyName || '--'}</dd>
          <dt className="text-ipc-muted">Tier</dt>
          <dd className="text-ipc-ink">{tierLabel(customer.tierName)}</dd>
          <dt className="text-ipc-muted">Customer ID</dt>
          <dd className="break-all font-mono text-ipc-ink">{customerId}</dd>
          <dt className="text-ipc-muted">Status</dt>
          <dd><StatusWord tone={STATUS_TONE[customerStatusTone(customer.status)] ?? 'off'}>{customerStatusLabel(customer.status)}</StatusWord></dd>
        </dl>
      </SectionCard>
      <CompanyBranding customerId={customerId} readOnly={operatorAccount} />
      <Notice
        tone="info"
        titleAs="p"
        title="Looking for the sending domain or Microsoft Teams tickets?"
        action={<Link to={notificationsPath(customerId, 'delivery')} className="tap inline-flex items-center text-sm font-semibold text-ipc-light hover:underline">Open Delivery</Link>}
        testId="moved-to-delivery"
      >
        They moved to Notifications, Delivery, because both decide where alerts go.
      </Notice>
    </div>
  );
}

/**
 * @param {{ customer: object, isOperator: boolean, operatorAccount: boolean, onRefresh?: () => void, notice?: import('react').ReactNode }} props
 */
export default function SettingsPage({ customer, isOperator, operatorAccount, onRefresh, notice = null }) {
  const location = useLocation();
  const navigate = useNavigate();
  const tabs = settingsTabs(isOperator);
  const base = customerSectionPath(customer.customerId, 'settings');
  const first = location.pathname.startsWith(base) ? location.pathname.slice(base.length).replace(/^\/+/, '').split('/')[0] : '';
  const active = tabs.find((t) => t.id === first) ?? tabs[0];
  const name = customer.companyName || customer.customerId;

  return (
    <div>
      <PageHeader title="Settings" subline={`Company, Microsoft connection and AWS deployment for ${name}.`} />
      <div className="space-y-4">
        {notice}
        <ConsoleTabs label="Settings" idPrefix="settings" items={tabs} value={active.id} onChange={(id) => navigate(`${base}/${id}`)} />
        <div {...panelProps('settings', active.id)}>
          <Routes>
            <Route index element={<Navigate to={`${base}/company`} replace />} />
            <Route path="company" element={<CompanyTab customer={customer} operatorAccount={operatorAccount} />} />
            <Route path="microsoft-connection" element={<div className="max-w-3xl"><ConsentCard customer={customer} isOperator={isOperator} onRefresh={onRefresh} /></div>} />
            <Route
              path="aws-deployment"
              element={(
                <div className="space-y-4">
                  <IdentityCard customer={customer} isOperator={isOperator} onRefresh={onRefresh} />
                  <DeploymentCard customer={customer} isOperator={isOperator} onRefresh={onRefresh} />
                </div>
              )}
            />
            {isOperator !== true ? <Route path="ipc-global-activity" element={<div className="max-w-4xl"><StaffActivity customerId={customer.customerId} /></div>} /> : null}
            {isOperator === true ? <Route path="danger-zone" element={<div className="max-w-3xl"><DangerZone customer={customer} onRefresh={onRefresh} /></div>} /> : null}
            <Route path="*" element={<Navigate to={`${base}/company`} replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}
